import {
  ExperimentOutlined,
  InfoCircleOutlined,
  LogoutOutlined,
  QuestionCircleOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import { type ItemType, type MenuDividerType } from 'antd5/es/menu/interface';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

import { Button, Dropdown, Header, Spin, useMessage, WarningAlert } from 'components/ui';
import { UserAvatar } from 'components/UserAvatar';
import { useGenericProgressContext } from 'horizon/components/GlobalProgressIndicator/GenericProgress/GenericProgressContext';
import { GlobalProgressButton } from 'horizon/components/GlobalProgressIndicator/GlobalProgressButton';
import { CopyUrlButton } from 'horizon/components/OrgRedirect';
import { useAccountContext } from 'utils/account/AccountContext';
import { useAuthContext } from 'utils/auth';
import { FlexContainer, SpacedContainer } from 'utils/layouts/containers';
import { MULTI_LANGUAGE, useDisableReleaseToggles } from 'utils/release-toggles';
import { notEmpty } from 'utils/types';

import { CustomerSwitcher } from './CustomerSwitcher';
import { useUploadContext } from './FileUploader';
import { LanguageSwitcher } from './LanguageSwitcher';
import { NewReleasesButton } from './NewReleases';
import { NotificationsButton } from './Notifications';

const StyledHeader = styled(Header)`
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: auto;
  min-height: 64px;
  padding: 1rem !important;
  background: ${({ theme }) => theme.secondaryBackgroundColor} !important;

  h1 {
    color: ${({ theme }) => theme.textColor} !important;
  }

  > div {
    &:nth-of-type(1) {
      line-height: 1.5;
    }
    &:nth-of-type(2) {
      height: calc(64px - 2rem);
    }
  }
`;

const horizonUserMenuItems = (
  handleLogout: () => void,
  canManageReleaseToggles: boolean,
  releaseTogglesDisabled: boolean,
  toggleDisabledReleaseToggles: () => void,
  t: (key: string) => string
): ItemType[] => {
  const menuItems: ItemType[] = [
    {
      key: 'user-guide',
      label: (
        <a
          href="https://guide.skyspecs.com/horizon/-MPobGVTLxgvA9kxtck7/"
          target="_blank"
          rel="noopener noreferrer"
        >
          {t('menu.userGuide', 'User Guide')}
        </a>
      ),
      icon: <InfoCircleOutlined />,
    },
    {
      key: 'support',
      label: (
        <a href="https://support.skyspecs.com/" target="_blank" rel="noopener noreferrer">
          {t('menu.support', 'Support')}
        </a>
      ),
      icon: <QuestionCircleOutlined />,
    },
    { key: 'divider-1', type: 'divider' } as MenuDividerType,
    {
      key: 'settings',
      label: <Link to="/settings">{t('navigation.settings', 'Settings')}</Link>,
      icon: <SettingOutlined />,
    },
    canManageReleaseToggles && {
      key: 'new-features',
      label: (
        <Link onClick={toggleDisabledReleaseToggles} to="#" data-testid="user-menu-release-toggles">
          {releaseTogglesDisabled
            ? t('menu.enableFeatures', 'Enable New Features')
            : t('menu.disableFeatures', 'Disable New Features')}
        </Link>
      ),
      icon: <ExperimentOutlined />,
    },
    canManageReleaseToggles && ({ key: 'divider-2', type: 'divider' } as MenuDividerType),
    {
      key: 'logout',
      label: t('menu.logout', 'Logout'),
      icon: <LogoutOutlined />,
      onClick: handleLogout,
    },
  ].filter(notEmpty);

  return menuItems;
};

export function GlobalHeader({ content }: { content: React.ReactNode }) {
  const { t, i18n } = useTranslation();
  const message = useMessage();
  const { canManageReleaseToggles, releaseTogglesDisabled, toggleDisabledReleaseToggles } =
    useDisableReleaseToggles();
  const [onLine, setOnLine] = useState(navigator.onLine);
  const { actionInProgress } = useGenericProgressContext();
  const { uploading, modalVisible, setConfirmProps } = useUploadContext();
  const { logout, user } = useAuthContext();
  const { hasReleaseToggle } = useAccountContext();
  const hasMultiLanguage = hasReleaseToggle(MULTI_LANGUAGE);

  // Defensive guard: if a user previously selected a non-English language and
  // later lost the multi-language toggle, the persisted choice would otherwise
  // keep rendering that language with no switcher to change it back. Reset to
  // English. This component only mounts after the account (and its toggles)
  // have loaded — the AccountContextProvider gates children behind a spinner —
  // so `hasMultiLanguage` is reliable here and we won't reset legitimate users.
  useEffect(() => {
    if (!hasMultiLanguage && i18n.language !== 'en') {
      localStorage.setItem('skyspecs-language', 'en');
      i18n.changeLanguage('en');
      // Reload so module-level translations (column/tab definitions evaluated at
      // import time) come back in English, matching LanguageSwitcher's behavior.
      window.location.reload();
    }
  }, [hasMultiLanguage, i18n]);

  const handleLogout = () => {
    if (uploading) {
      setConfirmProps({
        onOk: () => {
          // FIXME: Remove debug after logout problem is solved
          console.debug('GlobalHeader: logging out via Uploads in progress confirmation');
          logout({ broadcast: true });
        },
        message: t(
          'menu.uploadInProgress',
          'You have uploads in progress. Are you sure you want to log out?'
        ),
      });
    } else {
      // FIXME: Remove debug after logout problem is solved
      console.debug('GlobalHeader: logging out directly');
      logout({ broadcast: true });
    }
  };

  const displayConnectionStatus = useCallback(() => {
    if (navigator.onLine) {
      message.success(t('notifications.success.onlineRestored', 'You are back online'), 5);
    }

    setOnLine(navigator.onLine);
  }, [message, t]);

  const openUserGuide = useCallback((e: KeyboardEvent) => {
    // F1 for hot key
    if (e.code === 'F1') {
      // prevent chrome from opening help menu
      e.preventDefault();
      const win = window.open('https://guide.skyspecs.com');
      if (win) {
        win.opener = null;
      }
    }
  }, []);

  useEffect(() => {
    // notify online/offline
    window.addEventListener('offline', displayConnectionStatus);
    window.addEventListener('online', displayConnectionStatus);
    // for hotkeys
    window.addEventListener('keydown', openUserGuide);

    return () => {
      window.removeEventListener('offline', displayConnectionStatus);
      window.removeEventListener('online', displayConnectionStatus);
      window.removeEventListener('keydown', openUserGuide);
    };
  }, [displayConnectionStatus, openUserGuide]);

  return (
    <StyledHeader id="global-header-component" data-testid="global-header">
      <FlexContainer>{content}</FlexContainer>
      <FlexContainer>
        {!onLine && (
          <SpacedContainer>
            <WarningAlert
              showIcon={true}
              message={t('menu.connectionOffline', 'You are offline. Attempting to reconnect...')}
            />
          </SpacedContainer>
        )}

        {actionInProgress || (uploading && !modalVisible) ? (
          <SpacedContainer>
            <GlobalProgressButton />
          </SpacedContainer>
        ) : null}

        <SpacedContainer>
          <CopyUrlButton />
        </SpacedContainer>

        <NotificationsButton />

        <SpacedContainer>
          <NewReleasesButton />
        </SpacedContainer>

        {hasMultiLanguage && (
          <SpacedContainer>
            <LanguageSwitcher showTooltip={false} />
          </SpacedContainer>
        )}

        <CustomerSwitcher />

        <Dropdown
          className="user-menu-dropdown"
          menu={{
            items: horizonUserMenuItems(
              handleLogout,
              canManageReleaseToggles,
              releaseTogglesDisabled,
              toggleDisabledReleaseToggles,
              t
            ),
          }}
          trigger={['hover']}
        >
          <Button
            type="text"
            style={{ border: 'none', padding: 0, height: 'auto' }}
            data-testid="user-menu-icon"
            aria-label={t('menu.userMenu', 'User menu')}
          >
            {user ? <UserAvatar user={user} /> : <Spin size="small" />}
          </Button>
        </Dropdown>
      </FlexContainer>
    </StyledHeader>
  );
}
